import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};

const DEFAULT_SPEED_FACTOR = 25;

/** Matches the theme's mobile breakpoint. */
const MOBILE_QUERY = '(max-width: 749px)';

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The marquee items collection.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  async connectedCallback() {
    super.connectedCallback();

    const { marqueeItems } = this.refs;
    if (marqueeItems.length === 0) return;

    await this.#update(true);

    // A ResizeObserver on the host also catches the cases a window resize misses:
    // the marquee being laid out after connectedCallback, or starting inside a
    // hidden/zero-width container.
    this.#resizeObserver.observe(this);

    // The mobile speed factor changes at the breakpoint even when the marquee's own
    // width doesn't, which `#update` would otherwise skip as a vertical resize.
    this.#mobileQuery.addEventListener('change', this.#handleBreakpointChange);

    // Text width isn't final until webfonts have swapped in. Without a re-measure we
    // can end up one copy short and show a gap in the loop.
    if (document.fonts && document.fonts.status !== 'loaded') {
      document.fonts.ready.then(() => this.#update(true));
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#mobileQuery.removeEventListener('change', this.#handleBreakpointChange);
    this.#handleResize.cancel();
  }

  #mobileQuery = matchMedia(MOBILE_QUERY);

  #handleBreakpointChange = () => this.#update(true);

  /**
   * @type {{ cancel: () => void, current: number } | null}
   */
  #animation = null;

  /**
   * @type {number | null}
   */
  #marqueeWidth = null;

  #slowDown = debounce(() => {
    if (this.#animation) return;

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: 1,
      to: 0,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }, ANIMATION_OPTIONS.duration);

  #speedUp() {
    this.#slowDown.cancel();

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation || animation.playbackRate === 1) return;

    const from = this.#animation?.current ?? 0;
    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  /**
   * @param {number} value
   */
  #setSpeed(value) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  async #queryNumberOfCopies() {
    // Measure the original item rather than `refs.marqueeItems`, which also picks up the
    // clones we append (they carry the same `ref` attribute).
    const item = this.refs.content.firstElementChild;

    return new Promise((resolve) => {
      if (!item) {
        // Wrapping the resolve in a setTimeout here and below splits each marquee reflow into a separate task.
        return setTimeout(() => resolve({ numberOfCopies: 1, isHorizontalResize: true }), 0);
      }

      const intersectionObserver = new IntersectionObserver(
        (entries) => {
          const firstEntry = entries[0];
          if (!firstEntry) return;
          intersectionObserver.disconnect();

          // `rootBounds` is null in some browsers and zero before layout settles. Falling back
          // to 0 would give us zero copies at 0s speed, i.e. a static, half-empty marquee — so
          // measure the host directly instead. The `||` keeps the fast path reflow-free.
          const marqueeWidth = firstEntry.rootBounds?.width || this.clientWidth;
          const marqueeItemsWidth = firstEntry.boundingClientRect.width || item.getBoundingClientRect().width;

          const isHorizontalResize = this.#marqueeWidth !== marqueeWidth;
          this.#marqueeWidth = marqueeWidth;

          setTimeout(() => {
            resolve({
              numberOfCopies:
                marqueeWidth > 0 && marqueeItemsWidth > 0 ? Math.ceil(marqueeWidth / marqueeItemsWidth) : 1,
              isHorizontalResize,
            });
          }, 0);
        },
        { root: this }
      );
      intersectionObserver.observe(item);
    });
  }

  /**
   * Seconds for one loop, from the section/block settings. The mobile value is optional and
   * falls back to the desktop one.
   */
  get #speedFactor() {
    const mobile = this.#mobileQuery.matches ? Number(this.getAttribute('data-speed-factor-mobile')) : NaN;

    if (mobile > 0) return mobile;

    return Number(this.getAttribute('data-speed-factor')) || DEFAULT_SPEED_FACTOR;
  }

  /**
   * @param {number} numberOfCopies
   */
  #calculateSpeed(numberOfCopies) {
    const speedFactor = this.#speedFactor;
    const speed = Math.sqrt(Math.max(1, numberOfCopies)) * speedFactor;

    // An invalid duration invalidates the whole `animation` shorthand and the marquee stops dead.
    return Number.isFinite(speed) && speed > 0 ? speed : speedFactor;
  }

  /**
   * Re-measures the marquee and syncs the copy count and animation speed to it.
   *
   * @param {boolean} [force] - Run even when the width hasn't changed (initial render, font swap).
   */
  async #update(force = false) {
    const { content } = this.refs;
    const { numberOfCopies, isHorizontalResize } = await this.#queryNumberOfCopies();

    // opt out of marquee manipulation on vertical resizes
    if (!isHorizontalResize && !force) return;

    // Count the DOM directly — `refs` updates asynchronously and also includes the clones.
    const currentNumberOfCopies = content.children.length;

    if (numberOfCopies > currentNumberOfCopies) {
      this.#addRepeatedItems(numberOfCopies - currentNumberOfCopies);
    } else if (numberOfCopies < currentNumberOfCopies) {
      this.#removeRepeatedItems(currentNumberOfCopies - numberOfCopies);
    }

    this.#duplicateContent();
    this.#setSpeed(this.#calculateSpeed(numberOfCopies));
    this.#restartAnimation();
  }

  #resizeObserver = new ResizeObserver(() => this.#handleResize());

  #handleResize = debounce(() => this.#update(), 250);

  #restartAnimation() {
    const animations = this.refs.wrapper.getAnimations();

    requestAnimationFrame(() => {
      for (const animation of animations) {
        animation.currentTime = 0;
      }
    });
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');

    this.refs.wrapper.appendChild(clone);
  }

  /**
   * Appends `numberOfCopies` additional copies of the marquee items.
   *
   * @param {number} numberOfCopies
   */
  #addRepeatedItems(numberOfCopies) {
    const { content } = this.refs;
    const item = content.firstElementChild;

    if (!item) return;

    for (let i = 0; i < numberOfCopies; i++) {
      content.appendChild(item.cloneNode(true));
    }
  }

  /**
   * @param {number} numberOfCopies
   */
  #removeRepeatedItems(numberOfCopies) {
    const { content } = this.refs;
    const children = Array.from(content.children);

    const itemsToRemove = Math.min(numberOfCopies, children.length - 1);

    for (let i = 0; i < itemsToRemove; i++) {
      content.lastElementChild?.remove();
    }
  }
}

// Define the animateValue function
/**
 * Animate a numeric property smoothly.
 * @param {Object} params - The parameters for the animation.
 * @param {number} params.from - The starting value.
 * @param {number} params.to - The ending value.
 * @param {number} params.duration - The duration of the animation in milliseconds.
 * @param {function(number): void} params.onUpdate - The function to call on each update.
 * @param {function(number): number} [params.easing] - The easing function.
 * @param {function(): void} [params.onComplete] - The function to call when the animation completes.
 */
function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  /**
   * @param {number} currentTime - The current time in milliseconds.
   */
  function animate(currentTime) {
    if (cancelled) return;

    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);
    currentValue = from + (to - from) * easedProgress;

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === 'function') {
      onComplete();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
    },
  };
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
